import { Box } from '@chakra-ui/react';
import { useMemo } from 'react';

import { useThemeContext } from '$src/context/ThemeContext';
import { useIsDesktop } from '$src/hooks/client/useIsDesktop';

type ProgressBarProps = {
  progress: number;
  hide?: boolean;
};

/**
 *
 * @param param.progress: number - progress value in percentage
 * @returns JSX.Element
 */
export const ProgressBar = ({ progress, hide = true }: ProgressBarProps) => {
  const { themeName, brandColorPrimary } = useThemeContext();
  const { isMobile } = useIsDesktop();
  const isGlassy = themeName === 'glassy';
  const isSoft = themeName === 'soft';
  const isClassic = themeName === 'classic';
  const isCustom = themeName === 'custom';

  // Determine track background based on theme
  const trackBg = useMemo(() => {
    if (isGlassy) return 'var(--card-bg, rgba(255, 255, 255, 0.7))';
    if (isSoft) return 'var(--bg-secondary)';
    if (isClassic) return 'var(--border-gradient)';
    return 'var(--border, #E0E0E0)';
  }, [isGlassy, isSoft, isClassic]);

  // Determine progress indicator background based on theme
  const progressBg = useMemo(() => {
    if (isCustom && brandColorPrimary) {
      return brandColorPrimary;
    }
    if (isGlassy) {
      return 'linear-gradient(90deg, rgba(255, 255, 255, 0.70) 0%, rgba(255, 255, 255, 0.28) 100%)';
    }
    if (isSoft || isClassic) {
      // Soft/Classic theme: use solid color instead of aurora gradient
      return 'var(--border-gradient, #BEBBB4)';
    }
    // Aurora/Black: use aurora gradient
    return 'linear-gradient(90deg, #8DE690 2.08%, #A5F0A3 18.23%, #A6ECD7 33.33%, #97DADA 51.56%, #B3DAE7 67.71%, #B6C9EC 83.33%, #879ACB 100%)';
  }, [isCustom, brandColorPrimary, isGlassy, isSoft, isClassic]);

  return (
    <Box
      id="checkout-progress-bar"
      // Desktop is a two-column layout with the divider at right:49% (= 51% from
      // the left). Cap the bar to that left portion so it sits only over the
      // form (left) side, not the cart (right) side. Full width on mobile.
      w={isMobile ? 'full' : '51%'}
      h={isMobile ? '1px' : '9px'}
      overflow="hidden"
      // Glassy: keep a frosted look without `backdrop-filter` (avoids fixed/modal anchoring issues).
      bg={trackBg}
      border={isGlassy ? '1px solid rgba(255, 255, 255, 0.35)' : undefined}
      boxShadow={isGlassy ? 'inset 0 1px 0 rgba(255, 255, 255, 0.45)' : undefined}
    >
      <Box
        h="100%"
        position="relative"
        opacity={hide ? 0 : 1}
        width={`${progress}%`}
        transition="width 250ms ease-out, opacity 300ms ease-in-out"
        bg={progressBg}
        boxShadow={isGlassy ? 'inset 0 1px 0 rgba(255, 255, 255, 0.55)' : undefined}
        _before={
          isGlassy
            ? {
                content: `""`,
                position: 'absolute',
                inset: 0,
                pointerEvents: 'none',
                background: 'linear-gradient(180deg, rgba(255,255,255,0.45) 0%, rgba(255,255,255,0) 60%)',
                opacity: 0.65,
              }
            : undefined
        }
      />
    </Box>
  );
};
